import React, { useEffect, useRef, useState } from 'react';
import { createValuation, updateValuation } from '../api/valuations';
import { describeError } from '../lib/describeError';

// Modal save dialog. Three modes:
//   - "create" — POSTs a new valuation; the wizard's first save
//   - "update" — PATCHes name + inputs of a previously-saved valuation
//                (wizard's subsequent saves)
//   - "rename" — PATCHes only the name; used by the dashboard's row Rename
//
// The submit label and PATCH body differ; everything else (UI, validation,
// error mapping, dismiss-on-escape) is shared.
export default function SaveDialog({ mode = 'create', initialName = '', publicId, inputs, onSaved, onClose }) {
  const [name, setName] = useState(initialName);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('name_required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      let result;
      if (mode === 'rename') {
        result = await updateValuation(publicId, { name: trimmed });
      } else if (mode === 'update') {
        result = await updateValuation(publicId, { name: trimmed, inputs });
      } else {
        result = await createValuation({ name: trimmed, inputs, status: 'draft' });
      }
      onSaved(result);
    } catch (err) {
      setError((err && typeof err === 'object' && err.code) || 'save_failed');
      setSubmitting(false);
    }
  }

  const title = mode === 'rename'
    ? 'Rename valuation'
    : mode === 'update'
    ? 'Update valuation'
    : 'Save valuation';
  const submitLabel = mode === 'rename' ? 'Rename' : 'Save';

  return (
    <>
      <div style={overlayStyle} onClick={onClose} aria-hidden="true" />
      <div style={dialogStyle} role="dialog" aria-label={title}>
        <header style={headStyle}>
          <h2 style={titleStyle}>{title}</h2>
          <button type="button" onClick={onClose} style={closeBtnStyle} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        <form onSubmit={handleSubmit} style={formStyle}>
          <label htmlFor="valuation-name" style={labelStyle}>Name</label>
          <input
            id="valuation-name"
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); if (error) setError(null); }}
            placeholder="e.g., Memorial Onc-EHR pilot"
            style={error === 'name_taken' ? inputErrorStyle : inputStyle}
            disabled={submitting}
            aria-invalid={error === 'name_taken' || undefined}
          />
          {error && <div style={errorStyle}>{describeError('save', error)}</div>}

          <footer style={footStyle}>
            <button type="button" onClick={onClose} disabled={submitting} style={ghostBtnStyle}>
              Cancel
            </button>
            <button type="submit" disabled={submitting || !name.trim()} style={submitting ? accentDisabledStyle : accentBtnStyle}>
              {submitting ? 'Saving…' : submitLabel}
            </button>
          </footer>
        </form>
      </div>
    </>
  );
}

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(31, 26, 18, 0.45)', zIndex: 50 };
const dialogStyle = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '440px',
  maxWidth: '90vw',
  background: 'var(--paper)',
  border: '1px solid var(--line)',
  borderRadius: '8px',
  boxShadow: 'var(--shadow)',
  zIndex: 60,
  fontFamily: 'var(--font)',
};
const headStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px 14px', borderBottom: '1px solid var(--line)' };
const titleStyle = { margin: 0, fontFamily: 'var(--display)', fontWeight: 500, fontSize: '22px', color: 'var(--ink)', letterSpacing: '-0.015em' };
const closeBtnStyle = { width: '28px', height: '28px', borderRadius: '50%', border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink2)', display: 'grid', placeItems: 'center', cursor: 'pointer' };

const formStyle = { padding: '18px 22px 16px' };
const labelStyle = { display: 'block', fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' };
const inputStyle = { width: '100%', height: '44px', padding: '0 14px', border: '1px solid var(--line)', borderRadius: '4px', background: 'var(--surface)', fontSize: '14px', fontFamily: 'var(--font)', color: 'var(--ink)' };
const inputErrorStyle = { ...inputStyle, border: '1px solid var(--amber)', boxShadow: '0 0 0 3px rgba(245, 158, 11, 0.18)' };
const errorStyle = { marginTop: '10px', padding: '8px 10px', background: 'var(--amber-bg)', color: 'var(--amber-strong)', borderRadius: '4px', fontSize: '12.5px', lineHeight: 1.5 };

const footStyle = { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' };
const btnBase = { display: 'inline-flex', alignItems: 'center', height: '40px', padding: '0 18px', borderRadius: '999px', fontSize: '13.5px', fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font)' };
const ghostBtnStyle = { ...btnBase, background: 'transparent', border: '1px solid transparent', color: 'var(--ink2)' };
const accentBtnStyle = { ...btnBase, background: 'var(--accent)', border: '1px solid var(--accent)', color: '#fff' };
const accentDisabledStyle = { ...accentBtnStyle, background: 'var(--muted2)', border: '1px solid var(--muted2)', cursor: 'not-allowed' };
